import React from 'react';
import { Box, IBoxProps } from '@styleseat/ui';
import { supportsLazyImageAttribute } from '../../../modules/BrowserInfo';

type LazyImageProps = IBoxProps & {
  /**
   * URL for the image.
   */
  source?: string;

  /**
   * the image's alt text to help screen readers or other assistive devices
   */
  description: string;

  /**
   * if true then a placeholder with the same proportions will be rendered
   * while loading the image OR if the image has not yet been given a "source"
   */
  showPlaceholder?: boolean;

  /**
   * Color of the placeholder box
   */

  placeholderColor?: string;

  /**
   * image width: required for accurate aspect ratio
   * and placeholder rendering.
   */
  width: number;

  /**
   * image height: required for accurate aspect ratio
   * and placeholder rendering.
   */
  height: number;

  /**
   * Styles to apply to the image container
   */
  styles?: Record<string, any>;

  testID?: string;

  /**
   * Fallback image source when original produces an error
   */
  fallback?: string | null;
};

const lazySupport = supportsLazyImageAttribute();

/**
 * Renders an image that only loads when it's going to be
 * actually shown on screen
 * @param testID
 * @param source
 * @param description
 * @param width
 * @param height
 * @param styles
 * @param showPlaceholder
 * @param placeholderColor
 * @param restProps
 * @param borderRadius
 * @constructor
 */
const LazyImage = ({
  testID,
  source,
  description,
  width,
  height,
  styles,
  showPlaceholder,
  placeholderColor,
  fallback,
  ...restProps
}: LazyImageProps) => {
  const [loaded, setLoaded] = React.useState<boolean>(!lazySupport);
  const [imageSource, setImageSource] = React.useState<string>(source);
  const onLoaded = () => {
    setLoaded(true);
  };
  const onError = () => {
    if (fallback && imageSource !== fallback) {
      setImageSource(fallback);
    }
  };

  const displayPlaceholder: boolean = showPlaceholder && (!source || !loaded);

  return (
    <Box
      {...restProps}
      testID={testID}
      style={{
        width,
        height,
        ...styles,
        position: 'relative',
      }}
      maxWidth={width}
      maxHeight={height}
      color={placeholderColor}
    >
      {displayPlaceholder && (
        <div
          style={{
            flex: 1,
            position: 'absolute',
            top: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
          }}
        />
      )}
      {source && (
        <img
          loading="lazy"
          decoding="auto"
          alt={description}
          src={imageSource}
          onLoad={onLoaded}
          onError={onError}
          style={{
            position: 'absolute',
            top: 0,
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
          }}
          width={width}
          height={height}
        />
      )}
    </Box>
  );
};

export default LazyImage;
